"use client";

import React, { useMemo, useRef, useEffect, useState } from "react";
import { toast } from "react-toastify";
import bs58 from "bs58";
import { v4 as uuidv4 } from "uuid";
import {
  Connection,
  PublicKey,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import {
  getAssociatedTokenAddress,
  createTransferCheckedInstruction,
  createAssociatedTokenAccountInstruction,
} from "@solana/spl-token";
import { createFogoPayment, confirmFogoPayment } from "@/utils/api/fogo";
import RailButton from "@/components/ui/RailButton";

const MEMO_PROGRAM_ID = new PublicKey(
  "MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr"
);

function normalizeFogoIntent(init) {
  if (!init) return null;

  return {
    ...init,
    txnId: init?.txnId,
    purchaseId: init?.purchaseId,
    type: init?.type ?? init?.paymentType,
    tokenMint:
      init?.tokenMint ??
      init?.payment?.tokenMint ??
      init?.contracts?.tokenMint,
    treasuryTokenAccount:
      init?.treasuryTokenAccount ??
      init?.treasury?.tokenAccount ??
      init?.payment?.treasuryTokenAccount,
    amountMinor: init?.amountMinor ?? init?.payment?.amountMinor,
    reference:
      init?.reference ??
      init?.memo?.reference ??
      init?.payment?.reference,
    rpc: init?.rpc ?? init?.network?.rpc,
    decimals: init?.decimals ?? init?.payment?.decimals ?? 6,
  };
}

function getNightlyProvider() {
  if (typeof window === "undefined") return null;
  return window.nightly?.solana || (window.solana?.isNightly ? window.solana : null);
}

function getFeat(w, name) {
  return w?.features?.[name];
}

function getNightlyConnectFeat(n) {
  return getFeat(n, "standard:connect") || getFeat(n, "solana:connect");
}

function getNightlySignSendFeat(n) {
  return (
    getFeat(n, "standard:signAndSendTransaction") ||
    getFeat(n, "solana:signAndSendTransaction")
  );
}

function getNightlySignTxFeat(n) {
  return getFeat(n, "standard:signTransaction") || getFeat(n, "solana:signTransaction");
}

const pickChain = (acc) => {
  const chains = acc?.chains ?? [];
  return chains.find((c) => /fogo/i.test(c)) || chains[0] || "solana:mainnet";
};

async function nightlyConnect(nightly) {
  const feat = getNightlyConnectFeat(nightly);
  if (!feat) return null;

  let out;
  try {
    out = await feat.connect(false);
  } catch {
    out = await feat.connect({ silent: false });
  }

  const accounts = Array.isArray(out) ? out : out?.accounts;
  return accounts?.[0] || null;
}

export default function FogoPayButton({ product, user, onIntent, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState(null);
  const [err, setErr] = useState(null);
  const [walletPk, setWalletPk] = useState(null);
  const payLockRef = useRef(false);
  const intentPromiseRef = useRef(null);
  const clientRequestIdRef = useRef(null);
  const nightlyAccountRef = useRef(null);

  const onIntentRef = useRef(onIntent);
  useEffect(() => { onIntentRef.current = onIntent; }, [onIntent]);

  const dappUrl =
    typeof window !== "undefined" ? window.location.href : "https://reloadpi.com";

  const nightlyBrowseUrl = useMemo(() => {
    const encodedUrl = encodeURIComponent(dappUrl);
    return `nightly://v1?network=solana&cluster=mainnet&url=${encodedUrl}`;
  }, [dappUrl]);

  // Poll for Nightly provider
  useEffect(() => {
    if (typeof window === "undefined") return;

    const tick = () => {
      const n = getNightlyProvider();
      if (n?.publicKey) {
        setWalletPk(new PublicKey(n.publicKey.toString()));
      }
    };

    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, []);

  // Stable key: reset intent when payment inputs change
  const productKey = useMemo(() => {
    const bookId = product?.bookId ?? product?.metadata?.bookId ?? "";
    const offer = product?.offerId ?? "";
    const sku = product?.sku ?? product?.productSku ?? "";
    const amt = product?.amountUSD ?? product?.amountMajor ?? "";
    return `${bookId}:${offer}:${sku}:${amt}`;
  }, [product?.bookId, product?.metadata?.bookId, product?.offerId, product?.amountUSD, product?.amountMajor, product?.sku, product?.productSku]);

  useEffect(() => {
    clientRequestIdRef.current = uuidv4();
    setIntent(null);
    onIntentRef.current?.(null);
    intentPromiseRef.current = null;
  }, [productKey]);

  const connectWallet = async () => {
    const nightly = getNightlyProvider();

    if (!nightly) {
      toast.error("Nightly wallet not found. Please install or open in Nightly's browser.");
      return null;
    }

    try {
      const acc = await nightlyConnect(nightly);
      if (!acc?.publicKey) {
        toast.error("Nightly: no account returned. Did you approve the connection?");
        return null;
      }
      nightlyAccountRef.current = acc;
      const pubkey = new PublicKey(acc.publicKey);
      setWalletPk(pubkey);
      return pubkey;
    } catch (e) {
      console.error("Nightly connect failed:", e);
      toast.error(e?.message || "Nightly connect failed.");
      return null;
    }
  };

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
  const isNightlyUA = /Nightly/i.test(ua);
  const isInWalletBrowser = isNightlyUA || !!getNightlyProvider();
  const isWalletReady = !!walletPk;
  const isLoggedIn = !!(user?._id || user?.id || user?.sub || user?.email);

  const handlePay = async () => {
    if (payLockRef.current) return;
    payLockRef.current = true;

    try {
      if (!isLoggedIn) {
        toast.info("Please log in to continue.");
        setErr("Please log in to continue.");
        return;
      }

      setLoading(true);
      setErr(null);

      const nightly = getNightlyProvider();
      const nightlyConnectFeat = getNightlyConnectFeat(nightly);
      const nightlySignSendFeat = getNightlySignSendFeat(nightly);
      const nightlySignTxFeat = getNightlySignTxFeat(nightly);

      if (!nightlyConnectFeat) {
        toast.error("Nightly wallet not found. Please install or open in Nightly's browser.");
        return;
      }

      let acc = nightlyAccountRef.current || (await nightlyConnect(nightly));
      nightlyAccountRef.current = acc;

      if (!acc?.publicKey) {
        toast.error("Nightly: not authorized. Please approve the connection.");
        return;
      }

      const payerPk = new PublicKey(acc.publicKey);
      setWalletPk(payerPk);

      const email = user?.email || product?.recipient?.email || null;

      // Deduped intent fetch
      let rawInit = intent;
      if (!rawInit) {
        if (!intentPromiseRef.current) {
          const buyerWallet = payerPk.toBase58();
          const clientRequestId = clientRequestIdRef.current;

          intentPromiseRef.current = createFogoPayment({
            product,
            email,
            buyerWallet,
            clientRequestId,
          });
        }

        try {
          rawInit = await intentPromiseRef.current;
        } finally {
          intentPromiseRef.current = null;
        }
        setIntent(rawInit);
      }

      onIntentRef.current?.(rawInit);

      const init = normalizeFogoIntent(rawInit);
      const {
        txnId,
        purchaseId,
        type,
        tokenMint,
        treasuryTokenAccount,
        amountMinor,
        reference,
        rpc,
        decimals = 6,
      } = init || {};
      const id = purchaseId || txnId;
      const paymentType = type || (purchaseId ? "ebook" : null);

      if (!id || !paymentType || !tokenMint || !treasuryTokenAccount || !amountMinor || !reference || !rpc) {
        console.error("FOGO init missing required fields", { rawInit, normalized: init });
        throw new Error("FOGO init missing required fields");
      }

      // Build transaction
      const connection = new Connection(rpc, "confirmed");
      const mintPk = new PublicKey(tokenMint);
      const treasuryAtaPk = new PublicKey(treasuryTokenAccount);
      const userAta = await getAssociatedTokenAddress(mintPk, payerPk);
      const userAtaInfo = await connection.getAccountInfo(userAta);

      const parsedAcc = await connection.getParsedAccountInfo(userAta);
      const tokenAmountStr =
        parsedAcc?.value?.data?.parsed?.info?.tokenAmount?.amount ?? "0";
      const userBal = BigInt(tokenAmountStr);
      const needed = BigInt(String(amountMinor));

      if (userBal < needed) {
        const ui = parsedAcc?.value?.data?.parsed?.info?.tokenAmount?.uiAmount ?? 0;
        toast.error(
          userBal === 0n
            ? "No USDC in this wallet on this network. Deposit/bridge USDC then try again."
            : `Insufficient USDC. You have ${ui}, need ${Number(amountMinor) / 10 ** Number(decimals)}.`
        );
        return;
      }

      const ixs = [];

      // Memo first
      ixs.push(
        new TransactionInstruction({
          keys: [],
          programId: MEMO_PROGRAM_ID,
          data: Buffer.from(`order:${reference}`, "utf8"),
        })
      );

      if (!userAtaInfo) {
        ixs.push(
          createAssociatedTokenAccountInstruction(payerPk, userAta, payerPk, mintPk)
        );
      }

      ixs.push(
        createTransferCheckedInstruction(
          userAta,
          mintPk,
          treasuryAtaPk,
          payerPk,
          BigInt(String(amountMinor)),
          Number(decimals)
        )
      );

      const tx = new Transaction().add(...ixs);
      tx.feePayer = payerPk;
      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash("confirmed");
      tx.recentBlockhash = blockhash;

      const chain = pickChain(acc);
      const txBytes = tx.serialize({ requireAllSignatures: false, verifySignatures: false });

      let signature;

      if (nightlySignTxFeat) {
        const outputs = await nightlySignTxFeat.signTransaction({
          account: acc,
          transaction: txBytes,
          chain,
        });
        const first = Array.isArray(outputs) ? outputs[0] : outputs;
        const signedBytes = first?.signedTransaction;

        if (!(signedBytes instanceof Uint8Array)) {
          throw new Error("Nightly: signTransaction returned no signed tx");
        }

        signature = await connection.sendRawTransaction(signedBytes, {
          skipPreflight: false,
          preflightCommitment: "confirmed",
        });
      } else if (nightlySignSendFeat) {
        const outputs = await nightlySignSendFeat.signAndSendTransaction({
          account: acc,
          transaction: txBytes,
          chain,
        });
        const first = Array.isArray(outputs) ? outputs[0] : outputs;
        const sigBytes = first?.signature;

        if (!(sigBytes instanceof Uint8Array)) {
          throw new Error("Nightly: signAndSendTransaction returned no signature");
        }

        signature = bs58.encode(sigBytes);
      } else {
        throw new Error("Nightly: no signing feature available");
      }

      await connection.confirmTransaction(
        { signature, blockhash, lastValidBlockHeight },
        "confirmed"
      );

      const confirmed = await confirmFogoPayment({
        txnId,
        purchaseId,
        type: paymentType,
        signature,
      });

      toast.success("✅ Paid & delivered!");
      if (confirmed?.delivered || confirmed?.alreadyDelivered) {
        onSuccess?.(confirmed);
      }

      return confirmed;
    } catch (e) {
      if (e?.code === "AUTH") {
        toast.info("Please log in to continue.");
        setErr("Please log in to continue.");
        return;
      }
      console.error("❌ FOGO FULL ERROR:", e);
      const raw =
        e?.message ||
        e?.error?.message ||
        e?.data?.message ||
        e?.response?.data?.message ||
        "Fogo payment failed";
      setErr(raw);
      toast.error(`❌ ${raw}`);
    } finally {
      setLoading(false);
      payLockRef.current = false;
    }
  };

  return (
    <div className="mt-3 w-full animate-fadeIn">
      {!isWalletReady ? (
        <div className="flex flex-col gap-2">
          {isMobile && !isInWalletBrowser ? (
            <>
              <div className="rounded-lg border border-amber-700 bg-amber-900/30 p-3 text-xs text-amber-200">
                Open ReloadPi inside Nightly's in-app browser to connect your wallet.
              </div>

              <RailButton
                theme="fogo"
                onClick={() => window.location.assign(nightlyBrowseUrl)}
                badge="Nightly"
                hint="Recommended for Fogo checkout"
              >
                Open in Nightly
              </RailButton>

              <RailButton
                theme="neutral"
                onClick={async () => {
                  await navigator.clipboard.writeText(dappUrl);
                  toast.success("Link copied. Open Nightly's browser and paste it.");
                }}
                hint="Paste into Nightly's in-app browser"
              >
                Copy link for Nightly Browser
              </RailButton>

              <p className="text-xs text-textSecondary text-center opacity-80">
                If Nightly isn't installed, you'll be prompted to install it. Then reopen this page.
              </p>
            </>
          ) : (
            <RailButton
              theme="fogo"
              onClick={connectWallet}
              badge="Nightly / Solana"
              hint="Connect to pay with USDC"
            >
              Connect Nightly Wallet
            </RailButton>
          )}
        </div>
      ) : (
        <RailButton
          theme="fogo"
          onClick={handlePay}
          loading={loading}
          disabled={loading}
          badge="Nightly"
          hint="Direct on-chain transfer"
        >
          Pay with USDC (Fogo)
        </RailButton>
      )}

      <p className="text-xs text-textPrimary mt-2 text-center opacity-80">
        ⚡ Checkout via <span className="text-accent font-semibold">direct on-chain transfer</span> — fee paid in $FOGO.
      </p>

      {err && <p className="mt-2 text-xs text-red-400 text-center">{err}</p>}
    </div>
  );
}